/**
 * DevModeMenuItem
 *
 * 사이드바 프로필 메뉴의 개발자 모드 토글 — 설정 페이지의 스위치
 * (DeveloperModeSection)와 같은 스토어를 공유하는 빠른 진입점.
 * 표현은 ThemeCycleButton과 같은 관용구(확장: 행 + 상태 캡션 / 축소:
 * 툴팁 아이콘)를 따른다.
 */
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import { useDevModeStore } from '@/app/stores/devModeStore';

interface Props {
  expanded?: boolean;
}

export const DevModeMenuItem: React.FC<Props> = ({ expanded = true }) => {
  const devMode = useDevModeStore((s) => s.devMode);
  const setDevMode = useDevModeStore((s) => s.setDevMode);

  const handleClick = () => setDevMode(!devMode);

  if (expanded) {
    return (
      <ListItemButton
        onClick={handleClick}
        aria-label="개발자 모드 전환"
        aria-pressed={devMode}
        sx={{
          minHeight: 44,
          px: 2,
          gap: 1.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: devMode ? 'warning.main' : 'text.secondary' }}>
          <CodeIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">개발자 모드</Typography>
              <Typography
                variant="caption"
                color={devMode ? 'warning.main' : 'text.secondary'}
                sx={{ fontWeight: devMode ? 700 : 400 }}
              >
                {devMode ? '켜짐' : '꺼짐'}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
    );
  }

  return (
    <Tooltip title={`개발자 모드: ${devMode ? '켜짐' : '꺼짐'} (클릭해서 전환)`} placement="right">
      <ListItemButton
        onClick={handleClick}
        aria-label="개발자 모드 전환"
        aria-pressed={devMode}
        sx={{
          minHeight: 48,
          px: 1,
          justifyContent: 'center',
          color: devMode ? 'warning.main' : 'text.secondary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <CodeIcon fontSize="small" />
      </ListItemButton>
    </Tooltip>
  );
};

export default DevModeMenuItem;
